import {
  orderedVisibleReportSections,
  resolveSectionPresentation,
  DEFAULT_SECTION_PRESENTATION,
  UNREALIZED_FINDINGS_GROUP_BY,
  UNREALIZED_MEDIA_SUMMARY_GROUP_BY,
} from '../../lib/agentShared/formSchema';
import type { FormSchemaV2, ReportLayout } from '../../lib/agentShared/formSchema';
import type { ReportContent } from '../../lib/api/reports.gen';
import { ReportContentSchema } from '../../lib/api/reports.gen';
import { resolveReportLayoutPolicy } from '../../report-document';

// Placeholder `ReportContent` for the template editor's live preview (story
// 4-78). A draft template has no real inspection to render, so this builds
// the SAME shape `report.service.js#buildRenderContent` produces for a real
// submitted report — an Overview section, then one section per template
// section — against sample data instead of captured answers. This is the
// seam that lets the editor preview and a real report share the one
// `ReportDocument` component (`src/report-document`): identical shape in,
// identical component, only the data differs.
//
// ── Story 4-128b, defect D-8 — COMPLETE sample content, not partial ────────
// `docs/prd/reference/4-128-report-renderer/defects.md` D-8: "Report Studio's
// paginated preview renders a different content model from both real hosts."
//
// Until this story, this function hard-coded `scoreSummary: null`,
// `flaggedItems: null`, `actions: null` and `mediaSummary: null`. Every block
// those four feed is toggle-gated in `ReportDocument.tsx` by `content &&
// toggle`, so a null block meant the toggle could never have a visible effect.
// The measured result: **14 of the 90 shipped Report Studio controls had no
// observable effect in the one surface a template author uses to check their
// work** — `content.scores`, `content.flaggedItems`, `content.actions`,
// `content.mediaSummary`, `findingsGroupBy`, `colorRoles.findingSeverity`,
// `variants.finding`, `thumbnailSize` and all five `mediaSummary.*`
// presentation fields. An author could turn "Flagged items" on, see nothing
// change, and reasonably conclude the control was broken.
//
// That is also a problem for THIS epic specifically, and it is why the defect
// is owned here: `4-128c`–`4-128f` prove the shared renderer leaves every
// control's effect unchanged, and "unchanged" cannot be proven for an effect
// that was never visible in the first place.
//
// The four blocks are now populated with sample data — the same "sample data,
// real shape" treatment `subjectName`/`subjectAddress` have carried since
// 4-78, and the same contract a template author already reads everywhere else
// in this preview ("Sample property", "Sample response", "Sample" cells).
// Crucially this does NOT make the preview busier by default: each block still
// renders only when the author's own Content-tab toggle is on, so what the
// author sees is the effect of their own choice, which is the entire point of
// the control.
//
// ── Validated through the one shared protocol ─────────────────────────────
// P06 §4.3's render protocol accepts "validated normalized content — one
// shape, one validator, for all hosts", and defects.md's own stated correct
// behaviour is that this preview supply its sample content "through that same
// protocol". `ReportContentSchema` (the `.strict()` zod schema generated from
// `contracts/reports.js`, the SAME contract the API validates a real report's
// assembled content against) is that validator today, so this function now
// parses its own output through it before returning.
//
// That is not ceremony. A hand-built content object is exactly the thing that
// drifts from the contract silently — `.strict()` means a field renamed or
// dropped on the wire fails HERE, in the editor, at development time, instead
// of rendering a subtly different document from the one the worker will
// produce. `4-128c` replaces this call with the bundle's own entry-point
// validation; until then this is the same check, at the same boundary.
export function buildPreviewReportContent(schema: FormSchemaV2, layout: ReportLayout): ReportContent {
  // Story 4-114 — "a hidden section is confirmed absent from... the TOC" /
  // "reorder report sections independently from capture order", verified
  // against THIS live preview too (not just the real generated PDF): the
  // SAME resolver the backend uses for both the report body and its TOC
  // (`orderedVisibleReportSections`, `agents/shared/formSchema.js`) decides
  // which draft sections this preview shows and in what order.
  const reportSections = orderedVisibleReportSections(schema.sections);
  // The resolved "Report layout" panel config — the same six fields
  // `report.service.js#resolveReportStyle` resolves from this exact panel
  // for a real report, read here straight from the live draft.
  const style: ReportContent['style'] = {
    logo: layout.logoUrl || null,
    accentColor: layout.accentColor || null,
    font: layout.font,
    sectionHeaderStyle: layout.headerStyle,
    thumbnailSize: layout.thumbnailSize,
    pageSize: layout.pageSize,
    // Story 4-82 — read straight from the live draft, same as the six
    // fields above, so toggling a Style-tab control changes this exact
    // preview without a save round-trip.
    coverPage: layout.coverPage,
    headingScale: layout.headingScale,
    secondaryTextFromAccent: layout.secondaryTextFromAccent,
    // Story 4-85 — read straight from the live draft, same as the fields above.
    sectionUnderlineWidth: layout.sectionUnderlineWidth,
    surfaceRadius: layout.surfaceRadius,
    // Story 4-88a — read straight from the live draft, same as the fields
    // above. `footerPageNumber` has no visible effect in THIS preview (it
    // resolves through Puppeteer's own per-page footer in the downloaded
    // PDF only — a web preview has no discrete "pages" to number).
    // Story 4-107 — `letterhead.mode` rides along the same object; see
    // `ReportRender.tsx`'s own comment on why "every page" mode still
    // renders once here (a page-less preview has no discrete pages to
    // repeat onto until `4-108`'s pagination engine lands).
    letterhead: layout.letterhead,
    // Story 4-114 — read straight from the live draft, same as every field
    // above, so orientation/margin-preset controls change this exact
    // preview (both `ReportRender`'s injected `@page` rule and
    // `PagedReportPreview`'s pagination stylesheet, both driven by
    // `layoutPolicy` below) without a save round-trip.
    pageOrientation: layout.pageOrientation,
    marginsPreset: layout.marginsPreset,
    // Story 4-120 — read straight from the live draft, same as every field
    // above, so a Style Tools control changes this exact preview without a
    // save round-trip. `reportLayoutOf` already enum-guards all four (see
    // `agents/shared/formSchema.js#mergeTypographyRoles`/`mergeColorRoles`/
    // `mergeVariants`), so no further guarding is needed here — same trust
    // level every other field on this object already has.
    typographyRoles: layout.typographyRoles,
    colorRoles: layout.colorRoles,
    borderDensity: layout.borderDensity,
    variants: layout.variants,
  };
  const content: ReportContent = {
    title: schema.title || 'Untitled inspection report',
    accentColor: layout.accentColor || null,
    headerStyle: 'standard',
    style,
    // Story 4-107 — resolved from the SAME `style` object above, the
    // frontend twin of `report.service.js`'s own real-report resolution.
    layoutPolicy: resolveReportLayoutPolicy(style),
    templateName: schema.title || null,
    templateVersion: null,
    subjectName: 'Sample property',
    subjectAddress: '123 Sample Street, Example City',
    typeName: null,
    // Story 4-115 — sample values for the editor's own live preview, same
    // "sample data, real shape" pattern as `subjectName`/`subjectAddress`
    // immediately above. These have no visible effect in THIS preview (the
    // footer placeholders that read them resolve only in the downloaded PDF —
    // see `buildFooterTemplate`'s own comment) but keep this function's
    // return type-complete against the real `ReportContent` contract shape.
    inspectionStatus: 'in_progress',
    inspectionDate: new Date().toISOString(),
    generatedAt: null,
    reportReference: 'RPT-PREVIEW',
    branding: null,
    // ── Real content blocks (story 4-80; completed by 4-128b, defect D-8) ──
    // A draft template previews against SAMPLE data, not a real inspection.
    // Until story 4-128b these four were hard-coded `null`, which silently
    // disabled 14 of the 90 Report Studio controls in the one surface that
    // exposes them — see this file's own header comment for the full defect.
    // They now carry sample data, the same "sample data, real shape"
    // treatment `subjectName`/`subjectAddress` already had. Each still renders
    // only when the author's own Content-tab toggle is on.
    //
    // `tableOfContents` remains the one block this preview derives HONESTLY
    // rather than sampling: it is read straight from the template's own
    // section list, same as `report.service.js#tableOfContentsOf` reads it
    // from the real preset.
    //
    // `scores` → `ReportScoreStrip`. Deliberately not a round 100%: a full bar
    // shows neither the earned/possible split nor the partial fill, so it
    // would under-demonstrate the control it exists to demonstrate.
    scoreSummary: { earned: 34, possible: 40, percentage: 85 },
    // `flaggedItems` → `ReportFindingsRollup variant="flagged"`. Four items,
    // one per severity, because the controls this block exists to preview are
    // severity-shaped: `findingsGroupBy: 'severity'` has nothing to group
    // unless more than one severity is present, and
    // `colorRoles.findingSeverity: 'brand'` recolours a palette that needs all
    // four tones to be legible as a change. `evidenceCount` varies for the
    // same reason — a constant would hide the column.
    flaggedItems: {
      total: 4,
      bySeverity: { low: 1, medium: 1, high: 1, critical: 1 },
      items: [
        {
          observationText: 'Sample finding — sealant perished along the north elevation joint',
          recommendationText: 'Sample recommendation — rake out and reseal before the next wet season',
          severityTag: 'critical',
          evidenceCount: 3,
        },
        {
          observationText: 'Sample finding — handrail fixing loose at the second landing',
          recommendationText: 'Sample recommendation — refix and torque to specification',
          severityTag: 'high',
          evidenceCount: 2,
        },
        {
          observationText: 'Sample finding — extract fan runs noisily under load',
          recommendationText: 'Sample recommendation — service at next planned maintenance visit',
          severityTag: 'medium',
          evidenceCount: 1,
        },
        {
          observationText: 'Sample finding — minor scuffing to corridor skirting',
          recommendationText: null,
          severityTag: 'low',
          evidenceCount: 0,
        },
      ],
    },
    // `actions` → the SAME `ReportFindingsRollup` primitive, `variant="actions"`.
    // `ReportActionItem` has no `evidenceCount`; `recommendationText` is
    // non-nullable here (it IS the action), which is why this is a separate
    // shape rather than a slice of `flaggedItems` above.
    actions: {
      total: 2,
      items: [
        {
          observationText: 'Sample observation — sealant perished along the north elevation joint',
          recommendationText: 'Sample action — schedule remedial sealing within 30 days',
          severityTag: 'critical',
        },
        {
          observationText: 'Sample observation — handrail fixing loose at the second landing',
          recommendationText: 'Sample action — refix before the space is reoccupied',
          severityTag: 'high',
        },
      ],
    },
    tableOfContents: reportSections.map((section, index) => ({
      key: section.id || `section-${index}`,
      label: section.name || `Section ${index + 1}`,
    })),
    disclaimer: layout.staticBlocks.disclaimer || null,
    // Story 4-117 — real authored text, same "sample data, real shape"
    // treatment `disclaimer` already has (this preview has a real
    // template-level source for both now, unlike the sample subject/address
    // fields above).
    methodology: layout.staticBlocks.methodology || null,
    // `mediaSummary` → `ReportMediaSummaryGrid`. Story 4-128b, defect D-8 —
    // this gates SIX controls on its own (`thumbnailSize` and all five
    // `mediaSummary.*` presentation fields below), none of which could have a
    // visible effect while this was null.
    //
    // `thumbnailUrl` is deliberately `null` on every sample tile, and that is
    // not a shortcut. A draft template has no captured evidence, so any URL
    // here would be either a fabricated remote fetch at preview time (which
    // P06 §4.3 forbids at render time and which would fail offline) or a
    // bundled stock image pretending to be the author's own evidence. The grid
    // already renders a plain labelled "No preview" placeholder for a tile
    // with no thumbnail — its own documented, pre-existing graceful degrade —
    // and a placeholder TILE still demonstrates every control that is
    // genuinely being previewed here: tile size (`thumbnailSize`), `layout`,
    // `imageFit`'s box, `showCaptions`, `showTimestamps` and `groupBy`. The
    // one thing it cannot show is the photograph itself, which is the one
    // thing a draft template does not have.
    //
    // `fieldLabel`/`sectionName`/`capturedAt` vary across the six tiles
    // because they are exactly what `groupBy: 'question'`/`'section'` groups
    // on and what `showTimestamps` renders — a constant would make those
    // controls look broken for the second time.
    mediaSummary: {
      totalPhotos: 6,
      roomsWithPhotos: 3,
      photos: [
        {
          roomName: 'Sample area — front elevation',
          thumbnailUrl: null,
          caption: 'Sample caption — north joint, sealant condition',
          fieldLabel: 'Sample question — external condition',
          sectionName: 'Sample section — Exterior',
          capturedAt: '2026-01-15T09:12:00.000Z',
        },
        {
          roomName: 'Sample area — front elevation',
          thumbnailUrl: null,
          caption: 'Sample caption — render staining below the sill',
          fieldLabel: 'Sample question — external condition',
          sectionName: 'Sample section — Exterior',
          capturedAt: '2026-01-15T09:14:00.000Z',
        },
        {
          roomName: 'Sample area — stairwell',
          thumbnailUrl: null,
          caption: 'Sample caption — handrail fixing at the second landing',
          fieldLabel: 'Sample question — means of escape',
          sectionName: 'Sample section — Safety',
          capturedAt: '2026-01-15T10:02:00.000Z',
        },
        {
          roomName: 'Sample area — stairwell',
          thumbnailUrl: null,
          caption: 'Sample caption — landing lighting level',
          fieldLabel: 'Sample question — means of escape',
          sectionName: 'Sample section — Safety',
          capturedAt: '2026-01-15T10:05:00.000Z',
        },
        {
          roomName: 'Sample area — plant room',
          thumbnailUrl: null,
          caption: 'Sample caption — extract fan nameplate',
          fieldLabel: 'Sample question — mechanical services',
          sectionName: 'Sample section — Systems',
          capturedAt: '2026-01-15T11:20:00.000Z',
        },
        {
          roomName: 'Sample area — plant room',
          thumbnailUrl: null,
          caption: 'Sample caption — isolator labelling',
          fieldLabel: 'Sample question — mechanical services',
          sectionName: 'Sample section — Systems',
          capturedAt: '2026-01-15T11:24:00.000Z',
        },
      ],
    },
    // Story 4-119 — same "resolved the same way the real render downgrades
    // an unrealized value" treatment as `findingsGroupBy` immediately below:
    // `showLocation`/`showAnnotations` always resolve `false` regardless of
    // the stored value (see `agents/shared/formSchema.d.ts
    // #MediaSummaryPresentation`'s own comment for why neither has real data
    // behind it), and `groupBy: 'finding'` downgrades to `'none'`.
    mediaSummaryPresentation: {
      layout: layout.mediaSummary.layout,
      imageFit: layout.mediaSummary.imageFit,
      showCaptions: layout.mediaSummary.showCaptions,
      showTimestamps: layout.mediaSummary.showTimestamps,
      showLocation: false,
      showAnnotations: false,
      groupBy: UNREALIZED_MEDIA_SUMMARY_GROUP_BY.has(layout.mediaSummary.groupBy) ? 'none' : layout.mediaSummary.groupBy,
    },
    // The Content-tab toggle set (story 4-81) — read straight from the live
    // draft, same as `style` above, so toggling a checkbox in the editor
    // changes this exact preview without a save round-trip.
    contentToggles: layout.content,
    // Story 4-117 — resolved the SAME way `report.service.js
    // #resolveFindingsGroupBy` downgrades an unrealized value, so this
    // preview cannot show a grouping mode the real render would never apply.
    findingsGroupBy: UNREALIZED_FINDINGS_GROUP_BY.has(layout.findingsGroupBy) ? 'severity' : layout.findingsGroupBy,
    sections: [
      {
        key: 'overview',
        heading: 'Overview',
        kind: 'section',
        // Story 4-110 — a plain content section; 'auto' is today's default.
        pageBreak: 'auto',
        // Story 4-116 — a synthetic section, not a real template one; same
        // reasoning as `report.service.js#buildRenderContent`'s identical
        // 'overview' section comment.
        presentation: DEFAULT_SECTION_PRESENTATION,
        // Story 4-111 — not a template section (see `report.service.js
        // #buildRenderContent`'s identical 'overview' section comment).
        tocKey: null,
        columns: null,
        columnKeys: null,
        tableRows: null,
        body: null,
        rows: [
          { label: 'Site', value: 'Sample property', tone: 'neutral', kind: 'text', photos: null, chip: null, description: null },
          {
            label: 'Inspection status',
            value: 'in progress',
            tone: 'neutral',
            kind: 'text',
            photos: null,
            chip: null,
            description: null,
          },
          { label: 'Version', value: 'v1', tone: 'neutral', kind: 'text', photos: null, chip: null, description: null },
          { label: 'Report status', value: 'draft', tone: 'neutral', kind: 'text', photos: null, chip: null, description: null },
        ],
      },
      // `kind` (story 4-81) previews straight from the DRAFT section's own
      // kind, same as `tableOfContents` above — so toggling `tables` in the
      // editor shows real effect against sample data, not just against a
      // real submitted inspection.
      //
      // Story 4-110 — `pageBreak` previews the same way: straight from the
      // DRAFT section's own field (settable post-creation via
      // `EditorCanvas.tsx`'s per-section control), with the SAME
      // `kind:'page'` -> `'always'` migration fallback every other
      // resolver in this family applies to a section that predates the
      // field (`report.service.js#buildRenderContent`, `pdfExport.service
      // .js#renderSection`, `agents/shared/formSchema.js#indexAnswerSchema`).
      ...reportSections.map((section) => {
        const pageBreak = section.pageBreak ?? (section.kind === 'page' ? 'always' : 'auto');
        // Story 4-116 — same resolver `agents/shared/formSchema.js
        // #indexAnswerSchema` runs for a real report, applied here to the
        // live DRAFT section so this preview shows a presentation change
        // without a save round-trip, same as every other draft-preview field.
        // Story 4-117 — `layout.defaultShowEmpty` threaded through so this
        // draft preview shows the report-wide default's real effect too,
        // same "no save round-trip needed" preview contract as every other
        // field here.
        const presentation = resolveSectionPresentation(
          section,
          section.kind === 'table' ? 'table' : (section.kind ?? 'section'),
          layout.defaultShowEmpty,
        );
        return section.kind === 'table'
          ? {
              key: section.id,
              heading: section.name || 'Untitled table',
              kind: 'table' as const,
              pageBreak,
              presentation,
              // Story 4-111 — `entries.key`/`section.id` and this preview's
              // own `content.sections[].key` are the SAME namespace (unlike
              // the real `report.service.js#buildRenderContent`, which
              // prefixes with `answers-...` — see `ReportContentSection
              // .tocKey`'s own contract comment), so this is trivially the
              // template section's own id.
              tocKey: section.id,
              body: null,
              rows: [],
              columns: section.fields.map((field) => field.label || field.key),
              // Story 4-118 — same order/length as `columns` above, by
              // construction (both built from the same `section.fields`
              // array) — what `presentation.tableColumns` resolves against.
              columnKeys: section.fields.map((field) => field.key),
              // Two sample rows — enough to prove "a real table", not "table1/table2".
              // `kind`/`photos` (story 4-83c): this preview has no real
              // evidence to resolve (a draft template has no captured
              // answers yet), so every sample cell is plain text regardless
              // of the field's own response type — same as this preview
              // already did for every OTHER field type before this story.
              tableRows: [
                section.fields.map(() => ({ kind: 'text' as const, value: 'Sample', photos: null, chip: null })),
                section.fields.map(() => ({ kind: 'text' as const, value: 'Sample', photos: null, chip: null })),
              ],
            }
          : {
              key: section.id,
              heading: `${section.name || 'Untitled section'}${section.repeat ? ' (repeats)' : ''}`,
              kind: (section.kind === 'page' ? 'page' : 'section') as 'page' | 'section',
              pageBreak,
              presentation,
              // Story 4-111 — see the `kind:'table'` branch's own comment above.
              tocKey: section.id,
              columns: null,
              columnKeys: null,
              tableRows: null,
              body: null,
              rows: section.fields.map((field) => ({
                label: field.label || field.key,
                value: 'Sample response',
                tone: 'neutral' as const,
                kind: 'text' as const,
                photos: null,
                chip: null,
                // Story 4-117 — the real field description, so this preview
                // shows `showDescriptions`' actual effect against the
                // template's own authored text, not a placeholder.
                description: field.description || null,
              })),
            };
      }),
    ],
  };
  // Story 4-128b, defect D-8 — validated through the ONE shared content
  // protocol before it reaches the renderer (see this file's header comment).
  // `.parse`, not `.safeParse`: a draft preview that silently rendered content
  // the contract rejects is the exact class of frontend/backend divergence
  // this epic exists to close, and the editor is where a developer can still
  // see the failure. Every host that consumes this shape runs the same schema.
  return ReportContentSchema.parse(content) as ReportContent;
}
